'use client';

import React, { useState, useMemo } from 'react';
import {
  BookOpen,
  Trash2,
  Edit2,
  FileText,
  ExternalLink,
  Calendar,
  Clock,
  Building,
  Search,
  Filter,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { PDActivity, PDCycle, CATEGORY_INFO, PDActivityCategory, PDActivityType } from './types';
import { formatDate, minutesToHours, formatHours } from './utils';

interface PDActivityTableProps {
  activities: PDActivity[];
  cycle: PDCycle | null;
  onEdit: (activity: PDActivity) => void;
  onDelete: (activityId: string) => void;
}

type SortField = 'activityDate' | 'title' | 'duration' | 'category';
type SortDirection = 'asc' | 'desc';
type SourceFilter = 'ALL' | 'MANUAL' | 'SUPERVISION';

export function PDActivityTable({
  activities,
  cycle,
  onEdit,
  onDelete,
}: PDActivityTableProps) {
  // Search, filter, and sort state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<PDActivityType | 'ALL'>('ALL');
  const [filterCategory, setFilterCategory] = useState<PDActivityCategory | 'ALL'>('ALL');
  const [filterSource, setFilterSource] = useState<SourceFilter>('ALL');
  const [sortField, setSortField] = useState<SortField>('activityDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'cycle' | 'all'>('cycle');

  // Count supervision vs manual activities
  const supervisionCount = activities.filter(a => a.isFromSupervision).length;
  const manualCount = activities.length - supervisionCount;

  // Check if an activity is within the current cycle
  const isInCurrentCycle = (activity: PDActivity): boolean => {
    if (!cycle) return true;
    const activityDate = new Date(activity.activityDate);
    return activityDate >= cycle.start && activityDate <= cycle.end;
  };

  // Filter activities based on view mode
  const displayActivities = viewMode === 'cycle' && cycle
    ? activities.filter(isInCurrentCycle)
    : activities;

  // Count activities in current cycle (for showing in toggle)
  const cycleActivityCount = cycle
    ? activities.filter(isInCurrentCycle).length
    : activities.length;

  // Apply search, filter, and sort
  const filteredAndSortedActivities = useMemo(() => {
    let result = [...displayActivities];

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (a) =>
          a.title.toLowerCase().includes(query) ||
          a.provider?.toLowerCase().includes(query) ||
          a.description?.toLowerCase().includes(query)
      );
    }

    // Apply type filter
    if (filterType !== 'ALL') {
      result = result.filter((a) => a.activityType === filterType);
    }

    // Apply category filter
    if (filterCategory !== 'ALL') {
      result = result.filter((a) => a.category === filterCategory);
    }

    // Apply source filter
    if (filterSource !== 'ALL') {
      result = result.filter((a) => {
        if (filterSource === 'SUPERVISION') {
          return a.isFromSupervision === true;
        }
        return !a.isFromSupervision;
      });
    }

    // Apply sort
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'activityDate':
          comparison = new Date(a.activityDate).getTime() - new Date(b.activityDate).getTime();
          break;
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'duration':
          comparison = a.duration - b.duration;
          break;
        case 'category':
          comparison = CATEGORY_INFO[a.category].label.localeCompare(CATEGORY_INFO[b.category].label);
          break;
      }
      return sortDirection === 'desc' ? -comparison : comparison;
    });

    return result;
  }, [displayActivities, searchQuery, filterType, filterCategory, filterSource, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 text-gray-400" />;
    return sortDirection === 'asc' ? (
      <ArrowUp className="w-3 h-3 text-purple-600" />
    ) : (
      <ArrowDown className="w-3 h-3 text-purple-600" />
    );
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterType('ALL');
    setFilterCategory('ALL');
    setFilterSource('ALL');
  };

  const hasActiveFilters = searchQuery || filterType !== 'ALL' || filterCategory !== 'ALL' || filterSource !== 'ALL';

  if (activities.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-8 sm:p-12 text-center">
        <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          No Activities Yet
        </h3>
        <p className="text-gray-600 max-w-md mx-auto">
          Start logging your professional development activities to track your progress
          toward CRPO requirements.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      {/* Search and Filter Bar */}
      <div className="p-4 border-b border-gray-200 space-y-3">
        {/* View Mode Toggle */}
        {cycle && (
          <div className="flex items-center justify-between gap-3">
            <div
              className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50"
              role="tablist"
              aria-label="Activity view"
            >
              <button
                role="tab"
                aria-selected={viewMode === 'cycle'}
                onClick={() => setViewMode('cycle')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'cycle'
                    ? 'bg-white text-purple-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Current Cycle ({cycleActivityCount})
              </button>
              <button
                role="tab"
                aria-selected={viewMode === 'all'}
                onClick={() => setViewMode('all')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'all'
                    ? 'bg-white text-purple-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                All Activities ({activities.length})
              </button>
            </div>
            {viewMode === 'all' && activities.length !== cycleActivityCount && (
              <span className="text-xs text-gray-500 hidden sm:inline">
                Activities outside current cycle shown with indicator
              </span>
            )}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search activities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
              aria-label="Search activities"
            />
          </div>

          {/* Filter Toggle (Mobile) */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="sm:hidden flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            aria-expanded={showFilters}
            aria-controls="filter-panel"
          >
            <Filter className="w-4 h-4" />
            Filters
            {hasActiveFilters && (
              <span className="w-2 h-2 bg-purple-600 rounded-full" />
            )}
          </button>

          {/* Desktop Filters */}
          <div className="hidden sm:flex gap-2">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as PDActivityType | 'ALL')}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              aria-label="Filter by type"
            >
              <option value="ALL">All Types</option>
              <option value="DIDACTIC">Didactic</option>
              <option value="EXPERIENTIAL">Experiential</option>
            </select>

            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value as PDActivityCategory | 'ALL')}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              aria-label="Filter by category"
            >
              <option value="ALL">All Categories</option>
              {Object.entries(CATEGORY_INFO).map(([key, info]) => (
                <option key={key} value={key}>
                  {info.label}
                </option>
              ))}
            </select>

            {supervisionCount > 0 && (
              <select
                value={filterSource}
                onChange={(e) => setFilterSource(e.target.value as SourceFilter)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                aria-label="Filter by source"
              >
                <option value="ALL">All Sources</option>
                <option value="MANUAL">Manual ({manualCount})</option>
                <option value="SUPERVISION">Supervision ({supervisionCount})</option>
              </select>
            )}

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="px-3 py-2 text-sm text-purple-600 hover:text-purple-800"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Mobile Filters Panel */}
        {showFilters && (
          <div id="filter-panel" className="sm:hidden flex flex-col gap-2 pt-2">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as PDActivityType | 'ALL')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              aria-label="Filter by type"
            >
              <option value="ALL">All Types</option>
              <option value="DIDACTIC">Didactic</option>
              <option value="EXPERIENTIAL">Experiential</option>
            </select>

            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value as PDActivityCategory | 'ALL')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              aria-label="Filter by category"
            >
              <option value="ALL">All Categories</option>
              {Object.entries(CATEGORY_INFO).map(([key, info]) => (
                <option key={key} value={key}>
                  {info.label}
                </option>
              ))}
            </select>

            {supervisionCount > 0 && (
              <select
                value={filterSource}
                onChange={(e) => setFilterSource(e.target.value as SourceFilter)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                aria-label="Filter by source"
              >
                <option value="ALL">All Sources</option>
                <option value="MANUAL">Manual ({manualCount})</option>
                <option value="SUPERVISION">Supervision ({supervisionCount})</option>
              </select>
            )}

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="w-full px-3 py-2 text-sm text-purple-600 border border-purple-200 rounded-lg hover:bg-purple-50"
              >
                Clear Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Results Info */}
      {hasActiveFilters && (
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-sm text-gray-600">
          Showing {filteredAndSortedActivities.length} of {displayActivities.length} activities
        </div>
      )}

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-6 py-3">
                <button
                  onClick={() => handleSort('activityDate')}
                  className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                >
                  Date
                  <SortIcon field="activityDate" />
                </button>
              </th>
              <th className="text-left px-6 py-3">
                <button
                  onClick={() => handleSort('title')}
                  className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                >
                  Activity
                  <SortIcon field="title" />
                </button>
              </th>
              <th className="text-left px-6 py-3">
                <button
                  onClick={() => handleSort('category')}
                  className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                >
                  Category
                  <SortIcon field="category" />
                </button>
              </th>
              <th className="text-left px-6 py-3">
                <button
                  onClick={() => handleSort('duration')}
                  className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                >
                  Duration
                  <SortIcon field="duration" />
                </button>
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Provider
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Certificate
              </th>
              <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredAndSortedActivities.map((activity) => {
              const categoryInfo = CATEGORY_INFO[activity.category];
              const isDidactic = activity.activityType === 'DIDACTIC';
              const outsideCycle = viewMode === 'all' && !isInCurrentCycle(activity);
              const isSupervision = activity.isFromSupervision === true;

              return (
                <tr
                  key={activity.id}
                  className={`hover:bg-gray-50 transition-colors ${
                    outsideCycle ? 'bg-amber-50/50' : isSupervision ? 'bg-purple-50/30' : ''
                  }`}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-sm text-gray-900">
                      <Calendar className="w-4 h-4 text-gray-400" aria-hidden="true" />
                      {formatDate(activity.activityDate)}
                      {outsideCycle && (
                        <span
                          className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded"
                          title="This activity is outside your current cycle"
                        >
                          Past cycle
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="max-w-xs">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {activity.title}
                        </p>
                        {isSupervision && (
                          <span
                            className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded shrink-0"
                            title="Auto-logged from supervision"
                          >
                            Supervision
                          </span>
                        )}
                      </div>
                      {activity.description && (
                        <p className="text-xs text-gray-500 truncate mt-0.5">
                          {activity.description}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-2 h-2 rounded-full ${
                          isDidactic ? 'bg-blue-500' : 'bg-green-500'
                        }`}
                        aria-hidden="true"
                      />
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded ${
                          isDidactic
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {categoryInfo.label}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock className="w-4 h-4 text-gray-400" aria-hidden="true" />
                      {formatHours(minutesToHours(activity.duration))}h
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {activity.provider ? (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Building className="w-4 h-4 text-gray-400" aria-hidden="true" />
                        <span className="truncate max-w-[150px]">
                          {activity.provider}
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {activity.certificateUrl ? (
                      <a
                        href={activity.certificateUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-purple-600 hover:text-purple-800"
                      >
                        <FileText className="w-4 h-4" aria-hidden="true" />
                        <span className="truncate max-w-[100px]">
                          {activity.certificateFileName || 'View'}
                        </span>
                        <ExternalLink className="w-3 h-3" aria-hidden="true" />
                      </a>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {isSupervision ? (
                      <span className="text-xs text-gray-400">Auto-logged</span>
                    ) : (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => onEdit(activity)}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          aria-label={`Edit ${activity.title}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (
                              window.confirm(
                                'Are you sure you want to delete this activity?'
                              )
                            ) {
                              onDelete(activity.id);
                            }
                          }}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          aria-label={`Delete ${activity.title}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden divide-y divide-gray-200">
        {filteredAndSortedActivities.map((activity) => {
          const categoryInfo = CATEGORY_INFO[activity.category];
          const isDidactic = activity.activityType === 'DIDACTIC';
          const outsideCycle = viewMode === 'all' && !isInCurrentCycle(activity);
          const isSupervision = activity.isFromSupervision === true;

          return (
            <div key={activity.id} className={`p-4 space-y-3 ${
              outsideCycle ? 'bg-amber-50/50' : isSupervision ? 'bg-purple-50/30' : ''
            }`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="text-sm font-medium text-gray-900 truncate">
                      {activity.title}
                    </h4>
                    {isSupervision && (
                      <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded shrink-0">
                        Supervision
                      </span>
                    )}
                    {outsideCycle && (
                      <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded shrink-0">
                        Past cycle
                      </span>
                    )}
                  </div>
                  {activity.description && (
                    <p className="text-xs text-gray-500 truncate mt-0.5">
                      {activity.description}
                    </p>
                  )}
                </div>
                {isSupervision ? (
                  <span className="text-xs text-gray-400 shrink-0">Auto-logged</span>
                ) : (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => onEdit(activity)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                      aria-label={`Edit ${activity.title}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm('Are you sure you want to delete this activity?')) {
                          onDelete(activity.id);
                        }
                      }}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      aria-label={`Delete ${activity.title}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span
                  className={`px-2 py-1 font-medium rounded ${
                    isDidactic
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-green-100 text-green-700'
                  }`}
                >
                  {categoryInfo.label}
                </span>
                <span className="text-gray-500 flex items-center gap-1">
                  <Calendar className="w-3 h-3" aria-hidden="true" />
                  {formatDate(activity.activityDate)}
                </span>
                <span className="text-gray-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" aria-hidden="true" />
                  {formatHours(minutesToHours(activity.duration))}h
                </span>
              </div>

              {(activity.provider || activity.certificateUrl) && (
                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
                  {activity.provider && (
                    <span className="flex items-center gap-1">
                      <Building className="w-3 h-3" aria-hidden="true" />
                      {activity.provider}
                    </span>
                  )}
                  {activity.certificateUrl && (
                    <a
                      href={activity.certificateUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-purple-600 hover:text-purple-800"
                    >
                      <FileText className="w-3 h-3" aria-hidden="true" />
                      Certificate
                      <ExternalLink className="w-3 h-3" aria-hidden="true" />
                    </a>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Empty state for current cycle view with all activities option */}
      {displayActivities.length === 0 && viewMode === 'cycle' && activities.length > 0 && (
        <div className="p-8 text-center">
          <BookOpen className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 mb-3">No activities in current cycle</p>
          <button
            onClick={() => setViewMode('all')}
            className="text-sm text-purple-600 hover:text-purple-800 font-medium"
          >
            View all {activities.length} activities
          </button>
        </div>
      )}

      {/* Empty filtered state */}
      {filteredAndSortedActivities.length === 0 && hasActiveFilters && displayActivities.length > 0 && (
        <div className="p-8 text-center">
          <p className="text-gray-500 mb-2">No activities match your filters</p>
          <button
            onClick={clearFilters}
            className="text-sm text-purple-600 hover:text-purple-800"
          >
            Clear filters
          </button>
        </div>
      )}

      {/* Footer with summary */}
      <div className="bg-gray-50 border-t border-gray-200 px-4 sm:px-6 py-3">
        <p className="text-sm text-gray-600">
          {hasActiveFilters
            ? `Showing ${filteredAndSortedActivities.length} of ${displayActivities.length}`
            : `${displayActivities.length}`}{' '}
          activit{displayActivities.length === 1 ? 'y' : 'ies'}
          {cycle && viewMode === 'cycle' && (
            <span className="text-gray-400">
              {' '}
              in current cycle
            </span>
          )}
          {cycle && viewMode === 'all' && activities.length !== cycleActivityCount && (
            <span className="text-gray-400">
              {' '}
              ({cycleActivityCount} in current cycle)
            </span>
          )}
        </p>
      </div>
    </div>
  );
}
